import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../api'
import type { AccessGroup, Scope } from '../types'

const SCOPE_LABEL: Record<Scope, string> = {
  own: 'Только свои',
  all: 'Все',
}

export default function Groups() {
  const qc = useQueryClient()
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [newName, setNewName] = useState('')
  const [error, setError] = useState('')

  const groups = useQuery({ queryKey: ['groups'], queryFn: () => api.groups() })
  const catalog = useQuery({ queryKey: ['permissions'], queryFn: () => api.permissionCatalog() })

  const selected = groups.data?.find((g) => g.id === selectedId) ?? null

  // При первом заходе открываем первую группу, чтобы экран не был пустым
  useEffect(() => {
    if (selectedId === null && groups.data?.length) setSelectedId(groups.data[0].id)
  }, [groups.data, selectedId])

  const refresh = () => qc.invalidateQueries({ queryKey: ['groups'] })

  const create = useMutation({
    mutationFn: () =>
      api.createGroup({ name: newName.trim(), task_scope: 'own', client_scope: 'own', permissions: [] }),
    onSuccess: (g) => {
      setNewName('')
      setSelectedId(g.id)
      refresh()
    },
    onError: (e: any) => setError(e?.message ?? 'Не удалось создать группу'),
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      api.updateGroup(id, body),
    onSuccess: refresh,
    onError: (e: any) => setError(e?.message ?? 'Не удалось сохранить группу'),
  })

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteGroup(id),
    onSuccess: () => {
      setSelectedId(null)
      refresh()
    },
    onError: (e: any) => setError(e?.message ?? 'Не удалось удалить группу'),
  })

  function togglePermission(group: AccessGroup, code: string, on: boolean) {
    const next = on
      ? [...group.permissions, code]
      : group.permissions.filter((c) => c !== code)
    update.mutate({ id: group.id, body: { permissions: next } })
  }

  return (
    <>
      <h1 className="page-title">Группы доступа</h1>

      {error && (
        <div className="error" onClick={() => setError('')}>
          {error} (нажмите, чтобы скрыть)
        </div>
      )}

      <div className="toolbar">
        <div className="field" style={{ flex: 1, minWidth: 240 }}>
          <label>Новая группа</label>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Младший менеджер"
          />
        </div>
        <button className="btn primary" disabled={!newName.trim()} onClick={() => create.mutate()}>
          Создать
        </button>
      </div>

      <div className="groups-layout">
        <div className="groups-list">
          {groups.data?.map((g) => (
            <button
              key={g.id}
              className={`group-item${g.id === selectedId ? ' active' : ''}`}
              onClick={() => setSelectedId(g.id)}
            >
              <span className="group-item-name">{g.name}</span>
              <span className="group-item-meta">
                {g.is_admin ? 'полный доступ' : `${g.permissions.length} прав`} ·{' '}
                {g.users_count} чел.
              </span>
            </button>
          ))}
        </div>

        <div className="groups-detail">
          {!selected && <div className="empty">Выберите группу слева</div>}

          {selected && (
            <>
              <div className="grid-2">
                <div className="field">
                  <label>Название</label>
                  <input
                    key={`name-${selected.id}`}
                    defaultValue={selected.name}
                    onBlur={(e) => {
                      const v = e.target.value.trim()
                      if (v && v !== selected.name)
                        update.mutate({ id: selected.id, body: { name: v } })
                    }}
                  />
                </div>
                <div className="field">
                  <label>Описание</label>
                  <input
                    key={`desc-${selected.id}`}
                    defaultValue={selected.description ?? ''}
                    onBlur={(e) => {
                      if (e.target.value !== (selected.description ?? ''))
                        update.mutate({ id: selected.id, body: { description: e.target.value } })
                    }}
                  />
                </div>

                <div className="field">
                  <label>Какие задачи видит</label>
                  <select
                    value={selected.task_scope}
                    disabled={selected.is_admin}
                    onChange={(e) =>
                      update.mutate({ id: selected.id, body: { task_scope: e.target.value } })
                    }
                  >
                    {(['own', 'all'] as Scope[]).map((v) => (
                      <option key={v} value={v}>
                        {SCOPE_LABEL[v]}
                      </option>
                    ))}
                  </select>
                  <span className="hint-inline">
                    «Только свои» — задачи, где сотрудник исполнитель
                  </span>
                </div>

                <div className="field">
                  <label>Каких клиентов видит</label>
                  <select
                    value={selected.client_scope}
                    disabled={selected.is_admin}
                    onChange={(e) =>
                      update.mutate({ id: selected.id, body: { client_scope: e.target.value } })
                    }
                  >
                    {(['own', 'all'] as Scope[]).map((v) => (
                      <option key={v} value={v}>
                        {SCOPE_LABEL[v]}
                      </option>
                    ))}
                  </select>
                  <span className="hint-inline">
                    «Только свои» — клиенты, по которым у него есть задачи
                  </span>
                </div>
              </div>

              {selected.is_admin ? (
                <div className="warn" style={{ marginTop: 16 }}>
                  У группы администратора всегда полный доступ — иначе систему можно было бы
                  запереть, убрав право настраивать права. Галочки для неё не настраиваются.
                </div>
              ) : (
                <div className="perm-sections">
                  {catalog.data?.map((section) => (
                    <div key={section.section} className="perm-section">
                      <div className="perm-section-title">{section.section}</div>
                      {section.items.map((item) => (
                        <label key={item.code} className="checkbox-row perm-row">
                          <input
                            type="checkbox"
                            checked={selected.permissions.includes(item.code)}
                            onChange={(e) =>
                              togglePermission(selected, item.code, e.target.checked)
                            }
                          />
                          <span>{item.label}</span>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              <div className="modal-actions">
                {!selected.is_system && (
                  <button
                    className="btn danger"
                    onClick={() => {
                      if (confirm(`Удалить группу «${selected.name}»?`)) remove.mutate(selected.id)
                    }}
                  >
                    Удалить группу
                  </button>
                )}
                {selected.is_system && (
                  <span style={{ color: 'var(--muted)' }}>
                    Предустановленная группа: переименовать и настроить можно, удалить — нет
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
